/**
 * @fileoverview Publishing controls for the public portfolio (F8).
 *
 * The card is named after sharing, but the control that matters most is the
 * first one: this is a public page about a child and until now there was no
 * way to take it down. Everything else on the card is a narrower version of
 * the same question.
 *
 * Two rules the UI has to keep honest:
 *
 * - **The account-id link is always shown**, handle or no handle. It is the
 *   link that has already been shared, it never stops working, and hiding it
 *   the moment somebody claims a handle would suggest otherwise.
 * - **Section toggles say what stays**, not what is hidden. A list of switches
 *   with no consequence stated is a list nobody reads before flipping.
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import studentApi from "@/lib/studentApi";
import { getErrorMessage } from "@/lib/api";
import { Card, SectionHeader, Button } from "@/components/studio/primitives";
import { SToggle } from "@/components/studio/studentUi";

const SECTION_LABELS = {
  summary: "Overview and highlights",
  skills: "Skills",
  repertoire: "Repertoire",
  concepts: "Concepts",
  milestones: "Milestones",
  practice: "Practice hours and streak",
  goals: "Goals achieved",
  lessons: "Recent lessons",
  teachers: "Teachers",
};

export function portfolioUrl(origin, { handle, accountId }) {
  return handle ? `${origin}/p/${handle}` : `${origin}/portfolio/${accountId}`;
}

export function PortfolioShareCard() {
  const [settings, setSettings] = useState(null);
  const [draftHandle, setDraftHandle] = useState("");
  const [handleError, setHandleError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await studentApi.get("/student/portfolio/settings");
      setSettings(data);
      setDraftHandle(data.handle || "");
    } catch {
      setSettings(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!settings) return null;

  const save = async (payload) => {
    setSaving(true);
    setHandleError(null);
    try {
      const { data } = await studentApi.put("/student/portfolio/settings", payload);
      setSettings((s) => ({ ...s, ...data }));
      if (data.handle) setDraftHandle(data.handle);
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code) setHandleError(detail.message);
      else toast.error(getErrorMessage(err, "Couldn't save that"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const liveUrl = portfolioUrl(origin, { handle: settings.handle, accountId: settings.account_id });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(liveUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — select the link and copy it yourself");
    }
  };

  return (
    <Card className="mb-[22px]" data-testid="portfolio-share">
      <SectionHeader>Your public page</SectionHeader>

      <div className="flex items-center gap-4 py-3">
        <div className="flex-1">
          <div className="font-sans text-base font-medium text-[var(--text-primary)]">Anyone with the link can see it</div>
          <div className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">
            Turn this off and the page stops loading for everyone, including links you&rsquo;ve already shared.
          </div>
        </div>
        <SToggle
          on={settings.public}
          onToggle={() => save({ public: !settings.public })}
          ariaLabel="Public portfolio"
        />
      </div>

      {settings.public ? (
        <>
          <div className="border-t border-[var(--studio-divider)] pt-4">
            <label htmlFor="portfolio-handle" className="font-sans text-[13px] text-[var(--text-secondary)]">
              Your link
            </label>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="font-mono text-[13px] text-[var(--text-tertiary)]">{origin}/p/</span>
              <input
                id="portfolio-handle"
                value={draftHandle}
                onChange={(e) => setDraftHandle(e.target.value)}
                placeholder="your-name"
                data-testid="portfolio-handle-input"
                className="min-w-[160px] flex-1 rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-3 py-2 font-sans text-sm text-[var(--text-primary)]"
              />
              <Button
                onPress={() => save({ handle: draftHandle })}
                disabled={saving || !draftHandle.trim() || draftHandle === settings.handle}
                data-testid="portfolio-handle-save"
              >
                Save
              </Button>
            </div>
            {handleError ? (
              <p className="mt-2 font-sans text-[13px] text-[var(--state-danger)]" data-testid="portfolio-handle-error">
                {handleError}
              </p>
            ) : null}
            {/* Always shown. This is the link that has already been shared and
                it never stops working; hiding it once a handle exists would
                suggest otherwise. */}
            <p className="mt-3 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="portfolio-live-url">
              Live at{" "}
              <a href={liveUrl} target="_blank" rel="noopener noreferrer" className="underline">
                {liveUrl}
              </a>
              <button type="button" onClick={copy} className="ml-2 underline" data-testid="portfolio-copy">
                Copy
              </button>
            </p>
          </div>

          <div className="mt-5 border-t border-[var(--studio-divider)] pt-4">
            <p className="mb-2 font-sans text-[13px] text-[var(--text-secondary)]">What&rsquo;s on the page</p>
            {(settings.section_names || []).map((name) => (
              <div key={name} className="flex items-center gap-4 py-2">
                <div className="flex-1 font-sans text-[14px] text-[var(--text-primary)]">
                  {SECTION_LABELS[name] || name}
                </div>
                <SToggle
                  on={settings.sections?.[name] !== false}
                  onToggle={() =>
                    save({ sections: { ...settings.sections, [name]: settings.sections?.[name] === false } })
                  }
                  ariaLabel={SECTION_LABELS[name] || name}
                />
              </div>
            ))}
          </div>
        </>
      ) : null}
    </Card>
  );
}

export default PortfolioShareCard;
